import { auth } from "./firebase";
import { User } from "firebase/auth";
import { SchoolRole } from "@/types/school";
import { PLATFORM_NAME } from "./constants";

/**
 * Store Firebase auth token for cross-subdomain navigation
 */

const localLog = (...args: unknown[]) => {
  console.log("🔐 [Auth]", ...args);
};

export async function storeAuthToken(): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) {
    localLog("No user to store token for");
    return null;
  }

  try {
    const token = await user.getIdToken();

    // Store in cookie for cross-subdomain access
    if (typeof window !== "undefined") {
      const host = window.location.host;

      // For localhost development with PLATFORM_NAME.com subdomains,
      // we need to set domain to work across all subdomains
      const domain = calcDomain(host);

      localLog("About to store Firebase auth token:", {
        host,
        domain,
        tokenLength: token.length,
        tokenPreview: token.substring(0, 20) + "...",
        cookieWillBe: `firebaseAuthToken=${token.substring(
          0,
          10
        )}...; path=/; domain=${domain}; max-age=3600; SameSite=Lax`,
      });

      // Store token with 1 hour expiry
      document.cookie = `firebaseAuthToken=${token}; path=/; domain=${domain}; max-age=3600; SameSite=Lax`;

      // Verify the cookie was set
      const cookieWasSet = document.cookie.includes("firebaseAuthToken=");
      localLog("Firebase auth token storage verification:", {
        domain,
        tokenPreview: token.substring(0, 20) + "...",
        cookieWasSet,
        allCookiesAfter: document.cookie,
      });
    }

    return token;
  } catch (error) {
    console.error("Failed to store auth token:", error);
    return null;
  }
}

/**
 * Retrieve stored auth token from cookie
 */
export function getStoredAuthToken(): string | null {
  if (typeof window === "undefined") return null;

  const getCookie = (name: string): string | null => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(";").shift() || null;
    }
    return null;
  };

  const token = getCookie("firebaseAuthToken");
  if (token) {
    localLog("Retrieved stored auth token:", {
      tokenPreview: token.substring(0, 20) + "...",
    });
  }

  return token;
}

export function isValidAuthToken(): boolean {
  if (typeof window === "undefined") return false;

  const token = getStoredAuthToken();
  if (!token) {
    localLog("No stored auth token found");
    return false;
  }

  try {
    // Parse JWT token to check expiration
    const payload = JSON.parse(atob(token.split(".")[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    const isExpired = payload.exp < currentTime;

    localLog("Token validation:", {
      hasToken: true,
      expiresAt: new Date(payload.exp * 1000).toISOString(),
      currentTime: new Date(currentTime * 1000).toISOString(),
      isExpired,
      isValid: !isExpired,
      tokenPreview: token.substring(0, 30) + "...",
    });

    return !isExpired;
  } catch (error) {
    console.error("Failed to parse auth token:", error);
    return false;
  }
}

export async function getIdToken(): Promise<string | null> {
  const user: User | null = auth.currentUser;
  if (!user) return null;
  return await user.getIdToken();
}

export function onIdTokenChanged(callback: (token: string | null) => void) {
  auth.onIdTokenChanged(async (user) => {
    if (user) {
      const token = await user.getIdToken();
      callback(token);
    } else {
      callback(null);
    }
  });
}

// Issue a session token scoped to a school
export function issueSession(
  userId: string,
  schoolId: string,
  schoolSlug: string,
  roles: SchoolRole[]
): string {
  localLog("Issuing school session:", {
    userId,
    schoolId,
    schoolSlug,
    roles,
    timestamp: new Date().toISOString(),
  });

  // This would typically be implemented server-side
  // For now, we'll store this information in a cookie that works across subdomains
  const sessionData = {
    userId,
    schoolId,
    schoolSlug,
    roles,
    iat: Math.floor(Date.now() / 1000),
    exp: Math.floor(Date.now() / 1000) + 24 * 60 * 60, // 24 hours
  };

  // Store in cookie with domain set to work across subdomains
  const sessionString = JSON.stringify(sessionData);

  // Extract base domain for cookie (e.g., ".PLATFORM_NAME.com")
  if (typeof window !== "undefined") {
    const host = window.location.host;

    // For localhost development with PLATFORM_NAME.com subdomains,
    // we need to set domain to work across all subdomains
    const domain = calcDomain(host);
    localLog("About to store school session:", {
      host,
      domain,
      sessionData,
      sessionStringLength: sessionString.length,
      sessionPreview: sessionString.substring(0, 100) + "...",
      cookieWillBe: `schoolSession=${sessionString.substring(
        0,
        20
      )}...; path=/; domain=${domain}; max-age=${24 * 60 * 60}; SameSite=Lax`,
    });

    // Set cookie with domain to work across subdomains
    document.cookie = `schoolSession=${sessionString}; path=/; domain=${domain}; max-age=${
      24 * 60 * 60
    }; SameSite=Lax`;

    // Verify the cookie was set
    const cookieWasSet = document.cookie.includes("schoolSession=");
    localLog("School session storage verification:", {
      sessionData,
      domain,
      cookieWasSet,
      allCookiesAfter: document.cookie,
    });
  }

  // Also store in localStorage as fallback
  localStorage.setItem("schoolSession", sessionString);

  localLog("School session stored:", {
    sessionData,
    expiresAt: new Date(sessionData.exp * 1000).toISOString(),
  });

  return JSON.stringify(sessionData);
}

// Get current school session
export function getCurrentSchoolSession(): {
  userId: string;
  schoolId: string;
  schoolSlug: string;
  roles: SchoolRole[];
} | null {
  localLog("getCurrentSchoolSession called", {
    isClient: typeof window !== "undefined",
    timestamp: new Date().toISOString(),
    currentUrl: typeof window !== "undefined" ? window.location.href : "N/A",
  });

  if (typeof window === "undefined") {
    localLog("getCurrentSchoolSession called server-side, returning null");
    return null;
  }

  localLog("Checking cookies for school session", {
    allCookies: document.cookie.substring(0, 50) + "...",
    cookieCount: document.cookie.split(";").length,
  });

  // Helper function to get cookie value
  const getCookie = (name: string): string | null => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(";").shift() || null;
    }
    return null;
  };

  // Try to get session from cookie first, then localStorage
  let sessionData = getCookie("schoolSession");
  let source = "cookie";

  localLog("Cookie lookup result:", {
    found: !!sessionData,
    dataPreview: sessionData ? sessionData.substring(0, 50) + "..." : null,
  });

  if (!sessionData) {
    sessionData = localStorage.getItem("schoolSession");
    source = "localStorage";
    localLog("LocalStorage lookup result:", {
      found: !!sessionData,
      dataPreview: sessionData ? sessionData.substring(0, 50) + "..." : null,
    });
  }

  if (!sessionData) {
    localLog("No school session found in cookie or localStorage");
    return null;
  }

  try {
    localLog("Attempting to parse session data:", {
      source,
      dataLength: sessionData.length,
    });

    const parsed = JSON.parse(sessionData);

    localLog("Parsed session data:", {
      source,
      hasUserId: !!parsed.userId,
      hasSchoolId: !!parsed.schoolId,
      schoolSlug: parsed.schoolSlug,
      roles: parsed.roles,
      issuedAt: parsed.iat ? new Date(parsed.iat * 1000).toISOString() : "N/A",
      expiresAt: parsed.exp ? new Date(parsed.exp * 1000).toISOString() : "N/A",
      currentTime: new Date().toISOString(),
    });

    // Check if session is expired
    if (parsed.exp && parsed.exp < Math.floor(Date.now() / 1000)) {
      localLog("School session expired, removing from storage:", {
        source,
        expiredAt: new Date(parsed.exp * 1000).toISOString(),
        currentTime: new Date().toISOString(),
      });

      // Clear from both storages
      localStorage.removeItem("schoolSession");
      document.cookie = `schoolSession=; path=/; domain=${
        window.location.host.includes("localhost")
          ? window.location.host
          : `.${window.location.host.split(".").slice(-2).join(".")}`
      }; max-age=0`;

      return null;
    }

    localLog("Valid school session found and returned:", {
      source,
      schoolSlug: parsed.schoolSlug,
      roles: parsed.roles,
      userId: parsed.userId ? parsed.userId.substring(0, 8) + "..." : "N/A",
    });

    return parsed;
  } catch (err) {
    console.error("Failed to parse school session data:", {
      source,
      error: err,
      sessionDataPreview: sessionData.substring(0, 100) + "...",
    });
    return null;
  }
}

// Clear school session
export function clearSchoolSession(): void {
  localLog("Clearing school session from all storages");
  if (typeof window !== "undefined") {
    const sessionData = localStorage.getItem("schoolSession");
    if (sessionData) {
      try {
        const parsed = JSON.parse(sessionData);
        localLog("Clearing session for school:", {
          schoolSlug: parsed.schoolSlug,
          userId: parsed.userId,
        });
      } catch (err) {
        localLog("Clearing malformed session data");
      }
    }

    // Clear from localStorage
    localStorage.removeItem("schoolSession");

    // Clear from cookie
    const host = window.location.host;

    // Use same domain logic as other functions
    const domain = calcDomain(host);

    document.cookie = `schoolSession=; path=/; domain=${domain}; max-age=0`;

    localLog("School session cleared from both localStorage and cookie");
  } else {
    localLog("clearSchoolSession called server-side, skipping");
  }
}

const calcDomain = (host: string) => {
  let domain: string;
  if (host.includes("localhost") || host.includes("127.0.0.1")) {
    // For localhost, check if we're using PLATFORM_NAME.com subdomains
    if (host.includes(`${PLATFORM_NAME}.com`)) {
      domain = `.${PLATFORM_NAME}.com`; // Use base domain for cross-subdomain cookies
    } else {
      // localLog(host.split(":")[0]);
      domain = host; // Keep full localhost domain for standard localhost
      domain = host.split(":")[0]; // Keep full localhost domain for standard localhost
    }
  } else {
    // Fallback for production - extract base domain
    const hostParts = host.split(".");
    domain = hostParts.length >= 2 ? `.${hostParts.slice(-2).join(".")}` : host;
  }
  return domain;
};
